"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.professionOpenApiDocs = void 0;
// Profession payload schemas
const ProfessionCreatePayload = {
    type: 'object',
    description: 'Profession creation request parameters',
    properties: {
        name: {
            type: 'string',
            description: 'Profession name - descriptive title for the professional role',
            example: 'Software Developer',
            required: true
        },
        description: {
            type: 'string',
            description: 'Detailed description of the profession, including responsibilities and skills',
            example: 'Develops software applications and systems using various programming languages',
            required: false
        },
        active: {
            type: 'boolean',
            description: 'Whether the profession is currently active and available for selection',
            default: true,
            required: false
        }
    },
    required: ['name']
};
const ProfessionUpdatePayload = {
    type: 'object',
    description: 'Profession update request parameters',
    properties: {
        name: {
            type: 'string',
            description: 'Updated profession name',
            example: 'Senior Software Developer',
            required: false
        },
        description: {
            type: 'string',
            description: 'Updated profession description',
            example: 'Leads development teams and creates complex software solutions',
            required: false
        },
        active: {
            type: 'boolean',
            description: 'Updated profession status',
            required: false
        }
    }
};
const ProfessionResponse = {
    type: 'object',
    properties: {
        id: {
            type: 'number',
            description: 'Unique profession identifier'
        },
        name: {
            type: 'string',
            description: 'Profession name'
        },
        description: {
            type: 'string',
            description: 'Profession description'
        },
        common: {
            type: 'boolean',
            description: 'Whether the profession is marked as common (super admin only)'
        },
        active: {
            type: 'boolean',
            description: 'Whether the profession is active'
        },
        owner: {
            type: 'string',
            description: 'Owner user ID'
        },
        creator: {
            type: 'object',
            description: 'User who created this profession',
            properties: {
                id: { type: 'number' },
                firstname: { type: 'string' },
                lastname: { type: 'string' },
                username: { type: 'string' },
                email: { type: 'string' }
            }
        },
        branch: {
            type: 'object',
            description: 'Associated branch',
            properties: {
                id: { type: 'number' },
                name: { type: 'string' },
                description: { type: 'string' }
            }
        },
        image: {
            type: 'object',
            description: 'Profession image/logo',
            properties: {
                id: { type: 'number' },
                filename: { type: 'string' },
                url: { type: 'string' }
            }
        },
        knowledgeEntries: {
            type: 'array',
            description: 'Associated knowledge base entries',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'number' },
                    title: { type: 'string' },
                    description: { type: 'string' }
                }
            }
        },
        steps: {
            type: 'array',
            description: 'Onboarding steps for this profession',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'number' },
                    title: { type: 'string' },
                    description: { type: 'string' },
                    order: { type: 'number' }
                }
            }
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            description: 'Creation timestamp'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            description: 'Last update timestamp'
        }
    }
};
const ProfessionListResponse = {
    type: 'object',
    properties: {
        success: {
            type: 'boolean'
        },
        data: {
            type: 'array',
            items: ProfessionResponse
        },
        count: {
            type: 'integer',
            description: 'Total number of professions'
        }
    }
};
exports.professionOpenApiDocs = {
    'profession.list': {
        description: 'List all professions with optional search functionality',
        responses: {
            200: {
                description: 'Professions retrieved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: {
                            type: 'array',
                            items: ProfessionResponse
                        },
                        count: {
                            type: 'integer',
                            description: 'Total number of professions'
                        }
                    }
                }
            },
            500: {
                description: 'Internal server error - failed to retrieve professions',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5003
                    }
                }
            }
        }
    },
    'profession.create': {
        description: 'Create a new profession with name and description',
        payload: {
            body: ProfessionCreatePayload
        },
        responses: {
            200: {
                description: 'Profession created successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: ProfessionResponse
                    }
                }
            },
            400: {
                description: 'Bad request - validation error or missing required fields',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 9005
                    }
                }
            },
            500: {
                description: 'Internal server error - profession creation failed',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5003
                    }
                }
            }
        }
    },
    'profession.get': {
        description: 'Get profession by ID',
        responses: {
            200: {
                description: 'Profession retrieved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: ProfessionResponse
                    }
                }
            },
            404: {
                description: 'Profession not found',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5001
                    }
                }
            },
            500: {
                description: 'Internal server error',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5003
                    }
                }
            }
        }
    },
    'profession.update': {
        description: 'Update an existing profession\'s information',
        payload: {
            body: ProfessionUpdatePayload
        },
        responses: {
            200: {
                description: 'Profession updated successfully',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    data: {
                        type: 'object',
                        properties: ProfessionResponse
                    }
                }
            },
            400: {
                description: 'Bad request - validation error or invalid parameters',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 9005
                    }
                }
            },
            404: {
                description: 'Profession not found',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5001
                    }
                }
            },
            500: {
                description: 'Internal server error - profession update failed',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5004
                    }
                }
            }
        }
    },
    'profession.delete': {
        description: 'Delete profession by ID',
        responses: {
            200: {
                description: 'Profession deleted successfully',
                returnParams: {
                    success: {
                        type: 'boolean'
                    }
                }
            },
            404: {
                description: 'Profession not found',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5001
                    }
                }
            },
            500: {
                description: 'Internal server error - profession deletion failed',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5005
                    }
                }
            }
        }
    }
};
